import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { PhoneInput } from '@/components/PhoneInput';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useRole } from '@/hooks/UseRole';
import { useCompanyStore } from '@/stores/useCompanyStore';
import { useUserStore } from '@/stores/useUserStore';
import { Company } from '@/types/company';
import { User } from '@/types/user';
import { useGroupOptions } from '@/utils/groupOptions';
import { handleError } from '@/utils/handleError';
import { useRoleOptions } from '@/utils/roleOptions';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export const UpdateUserInformations = () => {
  const { t } = useTranslation();
  const groupOptions = useGroupOptions();
  const roleOptions = useRoleOptions();
  const {
    companies,
    loading: companiesLoading,
    listCompanies,
  } = useCompanyStore();
  const { user, updateUser, loading: userLoading } = useUserStore();
  const isAdmin = useRole('admin');

  const validationSchema = yup.object().shape({
    firstname: yup.string().required(t('common:validation.firstname.required')),
    lastname: yup.string().required(t('common:validation.lastname.required')),
    email: yup
      .string()
      .email(t('common:validation.email.format'))
      .required(t('common:validation.email.required')),
    phone: yup.string().required(t('common:validation.phone.required')),
    role: yup.string().required(t('common:validation.role.required')),
    groups: yup.array().of(yup.string()).notRequired(),
    avatar: yup.mixed().notRequired(),
    company_id: yup.number().notRequired(),
  });

  useEffect(() => {
    if (isAdmin) {
      listCompanies({ paginate: 0 });
    }
  }, [listCompanies, isAdmin]);

  if (!user) return <Loading />;

  const handleUpdateUser = async (
    values: User,
    { setErrors, setSubmitting }: FormikHelpers<User>,
  ) => {
    setSubmitting(true);
    try {
      await updateUser(values);
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Text className="text-center font-medium text-sky-800 md:text-left">
        {t('updateUser:information.title')}
      </Text>
      <Formik
        initialValues={user}
        validationSchema={validationSchema}
        onSubmit={handleUpdateUser}
      >
        {({ errors, touched, handleChange, values }) => {
          return (
            <Form className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <div className="space-y-1">
                <TextInput
                  label={t('updateUser:information.placeholder.firstname')}
                  name="firstname"
                  type="text"
                  placeholder={t(
                    'updateUser:information.placeholder.firstname',
                  )}
                  error={!!touched.firstname && !!errors.firstname}
                  isRequired
                />
              </div>
              <div className="space-y-1">
                <TextInput
                  label={t('updateUser:information.placeholder.lastname')}
                  name="lastname"
                  type="text"
                  placeholder={t('updateUser:information.placeholder.lastname')}
                  error={!!touched.lastname && !!errors.lastname}
                  isRequired
                />
              </div>
              <div className="space-y-1">
                <TextInput
                  label={t('updateUser:information.placeholder.email')}
                  name="email"
                  type="email"
                  placeholder={t('updateUser:information.placeholder.email')}
                  error={!!touched.email && !!errors.email}
                  isRequired
                />
              </div>
              <div className="space-y-1">
                <PhoneInput
                  label={t('updateUser:information.placeholder.phone')}
                  name="phone"
                  placeholder={t('updateUser:information.placeholder.phone')}
                  error={!!touched.phone && !!errors.phone}
                  isRequired
                />
              </div>
              <div className="space-y-1">
                <SelectInput
                  label={t('updateUser:information.placeholder.roles')}
                  name="role"
                  placeholder={t('updateUser:information.placeholder.roles')}
                  options={roleOptions}
                  defaultValue={values.role}
                  onChange={handleChange}
                  error={errors.role as string}
                  isRequired
                />
              </div>
              {isAdmin && (
                <>
                  <div className="space-y-1">
                    <SelectInput
                      label={t('updateUser:information.placeholder.groups')}
                      name="groups"
                      options={groupOptions}
                      defaultValue={values.groups}
                      onChange={handleChange}
                      error={errors.groups as string}
                      placeholder={t(
                        'updateUser:information.placeholder.groups',
                      )}
                      isSearchable
                      isMulti
                    />
                  </div>
                  <div className="space-y-1">
                    <SelectInput
                      label={t('updateUser:information.placeholder.company')}
                      name="company.id"
                      placeholder={t(
                        'updateUser:information.placeholder.company',
                      )}
                      options={companies.map((company: Company) => ({
                        value: company.id,
                        label: company.name,
                      }))}
                      defaultValue={values.company?.id || null}
                      onChange={handleChange}
                      error={errors.company}
                      isLoading={companiesLoading}
                      isClearable
                      isSearchable
                    />
                  </div>
                </>
              )}
              <div className="mt-4 sm:col-span-2">
                <Button
                  type="submit"
                  disabled={userLoading}
                  className="w-full lg:w-auto"
                >
                  {t('updateUser:information.submit')}
                </Button>
                <Text className="mt-2 text-sm font-light text-gray-light">
                  {t('updateUser:information.note')}
                </Text>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
